/* =============================================================
   Cardio Online Superstore — Floating Article Table of Contents
   v1.3.0
   -------------------------------------------------------------
   v1.3.0 changes:
     • H2s with H3 children are now collapsible. Each has a chevron
       toggle button. Default state: collapsed. The active section
       (per scrollspy) auto-expands so the reader sees where they are.
     • Clicking an H2 link both navigates AND expands its children.
     • Reduced-motion users get instant expand/collapse with no
       max-height animation.
   ============================================================= */
(function () {
  'use strict';

  var CONFIG = {
    articleSelectors:  ['.blog-post', '.article-content .content', 'article .content', 'article'],
    headingSelector:   'h2, h3',
    minHeadings:       3,
    activeRootMargin:  '-15% 0px -60% 0px',
    headerOffsetVar:   '--co-toc-header-offset',
    sidebarWidth:      240,
    sidebarGutter:     32,
    sidebarMinViewport: 1100
  };

  var PRM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var groupCounter = 0;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  function init() {
    var article = null;
    var headings = [];
    for (var s = 0; s < CONFIG.articleSelectors.length; s++) {
      var candidate = document.querySelector(CONFIG.articleSelectors[s]);
      if (!candidate) continue;
      var found = Array.prototype.slice.call(
        candidate.querySelectorAll(CONFIG.headingSelector)
      ).filter(function (h) {
        if (h.closest('.co-toc')) return false;
        return !h.hasAttribute('data-toc-skip');
      });
      if (found.length >= CONFIG.minHeadings) {
        article = candidate;
        headings = found;
        break;
      }
    }
    if (!article) return;

    var usedIds = Object.create(null);
    headings.forEach(function (h) {
      if (!h.id) h.id = uniqueSlug(h.textContent, usedIds);
      else usedIds[h.id] = true;
    });

    // Build a hierarchical tree: [{level, id, text, children: []}]
    var tree = buildTree(headings);

    document.documentElement.classList.add('co-has-toc');

    var sidebar = document.querySelector('[data-co-toc="sidebar"]');
    var sheet   = document.querySelector('[data-co-toc="sheet"]');
    var fab     = document.querySelector('[data-co-toc="fab"]');
    var fabLabel = fab && fab.querySelector('[data-co-toc="fab-label"]');
    var progressBar = fab && fab.querySelector('.co-toc-progress-bar');
    var backdrop = document.querySelector('[data-co-toc="backdrop"]');
    var closeBtn = sheet && sheet.querySelector('[data-co-toc="close"]');

    if (sidebar) renderTree(sidebar, tree);
    if (sheet)   renderTree(sheet.querySelector('.co-toc-sheet-body'), tree);

    // Wire toggles in both copies
    wireToggles(sidebar);
    wireToggles(sheet);

    // Flat list of items for scrollspy
    var items = flatten(tree);

    /* --- Sidebar layout --- */
    function updateSidebarLayout() {
      if (!sidebar) return;
      var rect = article.getBoundingClientRect();
      var viewport = window.innerWidth;
      var fits = viewport >= CONFIG.sidebarMinViewport
              && rect.left >= (CONFIG.sidebarWidth + CONFIG.sidebarGutter + 16);
      if (fits) {
        var leftPx = Math.max(16, rect.left - CONFIG.sidebarWidth - CONFIG.sidebarGutter);
        document.documentElement.style.setProperty('--co-toc-sidebar-left', leftPx + 'px');
        document.documentElement.classList.add('co-has-toc-sidebar');
        updateSidebarTop();
      } else {
        document.documentElement.classList.remove('co-has-toc-sidebar');
      }
    }
    function updateSidebarTop() {
      if (!sidebar) return;
      if (!document.documentElement.classList.contains('co-has-toc-sidebar')) return;
      var headerOffset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(CONFIG.headerOffsetVar) || '72',
        10
      );
      var minTop = headerOffset + 32;
      var topPx = Math.max(minTop, article.getBoundingClientRect().top);
      sidebar.style.top = topPx + 'px';
    }
    updateSidebarLayout();
    window.addEventListener('resize', throttleRaf(updateSidebarLayout));
    window.addEventListener('scroll', throttleRaf(updateSidebarTop), { passive: true });

    /* --- Link click: smooth scroll + auto-expand if it's an H2 with children --- */
    function onLinkClick(e) {
      var link = e.target.closest('.co-toc-link');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      var target = document.getElementById(href.slice(1));
      if (!target) return;
      e.preventDefault();

      // If this link belongs to an H2 with children, expand them
      ensureExpanded(link);

      var headerOffset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(CONFIG.headerOffsetVar) || '72',
        10
      );
      var y = target.getBoundingClientRect().top + window.pageYOffset - (headerOffset + 16);
      window.scrollTo({ top: y, behavior: PRM ? 'auto' : 'smooth' });
      history.replaceState(null, '', href);
      closeSheet();
    }
    if (sidebar) sidebar.addEventListener('click', onLinkClick);
    if (sheet)   sheet.addEventListener('click', onLinkClick);

    /* --- Scrollspy --- */
    var linkById = {};
    function collectLinks(root) {
      if (!root) return;
      root.querySelectorAll('.co-toc-link').forEach(function (a) {
        var id = (a.getAttribute('href') || '').slice(1);
        if (!id) return;
        if (!linkById[id]) linkById[id] = [];
        linkById[id].push(a);
      });
    }
    collectLinks(sidebar);
    collectLinks(sheet);

    var activeId = null;
    function setActive(id) {
      if (id === activeId) return;
      if (activeId && linkById[activeId]) {
        linkById[activeId].forEach(function (a) { a.removeAttribute('aria-current'); });
      }
      activeId = id;
      if (id && linkById[id]) {
        linkById[id].forEach(function (a) {
          a.setAttribute('aria-current', 'location');
          ensureExpanded(a); // auto-expand parent if needed
        });
        if (fabLabel) {
          var item = items.find(function (it) { return it.id === id; });
          fabLabel.textContent = item ? 'Contents · ' + item.text : 'Contents';
        }
      } else if (fabLabel) {
        fabLabel.textContent = 'Contents';
      }
    }

    if ('IntersectionObserver' in window) {
      var visible = new Set();
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        var firstVisible = null;
        for (var i = 0; i < items.length; i++) {
          if (visible.has(items[i].id)) { firstVisible = items[i].id; break; }
        }
        if (firstVisible) setActive(firstVisible);
        else {
          var above = null;
          for (var j = 0; j < items.length; j++) {
            var rect = items[j].el.getBoundingClientRect();
            if (rect.top < window.innerHeight * 0.4) above = items[j].id;
            else break;
          }
          if (above) setActive(above);
        }
      }, { rootMargin: CONFIG.activeRootMargin, threshold: 0 });
      items.forEach(function (it) { io.observe(it.el); });
    }

    /* --- Progress ring --- */
    var CIRC = 75.4;
    function updateProgress() {
      if (!progressBar) return;
      var articleRect = article.getBoundingClientRect();
      var start = articleRect.top + window.pageYOffset;
      var end   = start + article.offsetHeight - window.innerHeight;
      var ratio = Math.max(0, Math.min(1, (window.pageYOffset - start) / Math.max(1, end - start)));
      progressBar.style.strokeDashoffset = CIRC * (1 - ratio);
    }
    window.addEventListener('scroll', throttleRaf(updateProgress), { passive: true });
    window.addEventListener('resize', throttleRaf(updateProgress));
    updateProgress();

    function updateFabVisibility() {
      if (!fab) return;
      var articleTop = article.getBoundingClientRect().top;
      if (articleTop < window.innerHeight * 0.5) {
        document.documentElement.classList.remove('co-toc-hidden');
      } else {
        document.documentElement.classList.add('co-toc-hidden');
      }
    }
    window.addEventListener('scroll', throttleRaf(updateFabVisibility), { passive: true });
    updateFabVisibility();

    /* --- Sheet open/close --- */
    var lastFocused = null;
    function openSheet() {
      if (!sheet) return;
      lastFocused = document.activeElement;
      sheet.setAttribute('aria-hidden', 'false');
      if (fab) fab.setAttribute('aria-expanded', 'true');
      if (backdrop) backdrop.setAttribute('data-open', 'true');
      document.documentElement.classList.add('co-toc-sheet-open');
      var firstLink = sheet.querySelector('.co-toc-link');
      if (firstLink) firstLink.focus();
      document.addEventListener('keydown', onKeyDown);
    }
    function closeSheet() {
      if (!sheet) return;
      if (sheet.getAttribute('aria-hidden') === 'true') return;
      sheet.setAttribute('aria-hidden', 'true');
      if (fab) fab.setAttribute('aria-expanded', 'false');
      if (backdrop) backdrop.setAttribute('data-open', 'false');
      document.documentElement.classList.remove('co-toc-sheet-open');
      if (lastFocused && lastFocused.focus) lastFocused.focus();
      document.removeEventListener('keydown', onKeyDown);
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') { closeSheet(); return; }
      if (e.key === 'Tab') {
        var focusable = sheet.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        var first = focusable[0], last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    if (fab)      fab.addEventListener('click', openSheet);
    if (closeBtn) closeBtn.addEventListener('click', closeSheet);
    if (backdrop) backdrop.addEventListener('click', closeSheet);

    if (sheet) {
      var startY = null;
      var grabber = sheet.querySelector('.co-toc-sheet-grabber');
      var header  = sheet.querySelector('.co-toc-sheet-header');
      var swipeZone = grabber && header ? [grabber, header] : [];
      swipeZone.forEach(function (zone) {
        zone.addEventListener('touchstart', function (e) { startY = e.touches[0].clientY; }, { passive: true });
        zone.addEventListener('touchmove', function (e) {
          if (startY === null) return;
          var dy = e.touches[0].clientY - startY;
          if (dy > 60) { startY = null; closeSheet(); }
        }, { passive: true });
        zone.addEventListener('touchend', function () { startY = null; });
      });
    }
  }

  /* -------------------------------------------------
     Tree building + rendering
     ------------------------------------------------- */
  function buildTree(headings) {
    var tree = [];
    var currentH2 = null;
    headings.forEach(function (h) {
      var level = parseInt(h.tagName.substring(1), 10);
      var node = { level: level, id: h.id, text: (h.textContent || '').trim(), el: h, children: [] };
      if (level === 2) {
        tree.push(node);
        currentH2 = node;
      } else if (level === 3 && currentH2) {
        currentH2.children.push(node);
      } else {
        // Orphan H3 before any H2 — promote to top level
        tree.push(node);
      }
    });
    return tree;
  }

  function flatten(tree) {
    var out = [];
    tree.forEach(function (n) {
      out.push(n);
      if (n.children && n.children.length) {
        n.children.forEach(function (c) { out.push(c); });
      }
    });
    return out;
  }

  function renderTree(container, tree) {
    if (!container) return;
    var ul = document.createElement('ul');
    ul.className = 'co-toc-list';
    tree.forEach(function (node) {
      ul.appendChild(renderNode(node));
    });
    container.appendChild(ul);
  }

  function renderNode(node) {
    var li = document.createElement('li');
    li.dataset.level = node.level;

    var hasChildren = node.children && node.children.length > 0;

    if (hasChildren) {
      li.className = 'co-toc-item co-toc-item--has-children';
      var groupId = 'co-toc-grp-' + (++groupCounter);

      var row = document.createElement('div');
      row.className = 'co-toc-row';

      var a = makeLink(node);
      row.appendChild(a);

      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'co-toc-toggle';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', groupId);
      toggle.setAttribute('aria-label', 'Expand ' + node.text + ' subsections');
      toggle.innerHTML = '<svg class="co-toc-toggle-chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
        '<path d="M6 4 L10 8 L6 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';
      row.appendChild(toggle);

      li.appendChild(row);

      var childUl = document.createElement('ul');
      childUl.className = 'co-toc-children';
      childUl.id = groupId;
      childUl.style.maxHeight = '0px';
      node.children.forEach(function (child) {
        childUl.appendChild(renderNode(child));
      });
      li.appendChild(childUl);
    } else {
      li.appendChild(makeLink(node));
    }
    return li;
  }

  function makeLink(node) {
    var a = document.createElement('a');
    a.className = 'co-toc-link';
    a.href = '#' + node.id;
    a.textContent = node.text;
    return a;
  }

  /* -------------------------------------------------
     Toggle wiring + auto-expand helper
     ------------------------------------------------- */
  function wireToggles(root) {
    if (!root) return;
    root.querySelectorAll('.co-toc-toggle').forEach(function (toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var li = toggle.closest('.co-toc-item--has-children');
        if (!li) return;
        var children = li.querySelector('.co-toc-children');
        if (!children) return;
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          collapse(li, toggle, children);
        } else {
          expand(li, toggle, children);
        }
      });
    });
  }

  function expand(li, toggle, children) {
    toggle.setAttribute('aria-expanded', 'true');
    li.classList.add('is-expanded');
    if (PRM) {
      children.style.maxHeight = 'none';
    } else {
      children.style.maxHeight = children.scrollHeight + 'px';
    }
  }
  function collapse(li, toggle, children) {
    toggle.setAttribute('aria-expanded', 'false');
    li.classList.remove('is-expanded');
    if (PRM) {
      children.style.maxHeight = '0px';
    } else {
      // Force layout before transitioning back to 0 so animation fires
      children.style.maxHeight = children.scrollHeight + 'px';
      requestAnimationFrame(function () {
        children.style.maxHeight = '0px';
      });
    }
  }

  // For a given anchor element, expand its parent group if any.
  // Walks UP through any nested .co-toc-item--has-children ancestors.
  function ensureExpanded(linkElement) {
    var current = linkElement;
    while (current && current !== document.body) {
      if (current.classList && current.classList.contains('co-toc-item--has-children')) {
        var toggle = current.querySelector(':scope > .co-toc-row > .co-toc-toggle');
        var children = current.querySelector(':scope > .co-toc-children');
        if (toggle && children && toggle.getAttribute('aria-expanded') !== 'true') {
          expand(current, toggle, children);
        }
      }
      current = current.parentNode;
    }
  }

  /* -------------------------------------------------
     Utility
     ------------------------------------------------- */
  function uniqueSlug(text, used) {
    var base = (text || 'section')
      .toLowerCase()
      .normalize('NFKD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 64) || 'section';
    var slug = base;
    var n = 2;
    while (used[slug]) { slug = base + '-' + n++; }
    used[slug] = true;
    return slug;
  }

  function throttleRaf(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { fn(); ticking = false; });
    };
  }
})();
